/**
 * AvailabilityPanel
 * Main availability management page with two tabs:
 *   - Wochenplan (weekly schedule)
 *   - Abwesenheiten (date overrides)
 */
import { useState, useMemo } from "react";
import { useQuery } from "convex/react";
import { api } from "@jourfx/convex/client";
import { useAuth } from "@/hooks/useAuth";
import { CalendarDays, CalendarOff, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, Label, Skeleton } from "@jourfx/ui";
import { WeeklyScheduleEditor } from "./WeeklyScheduleEditor";
import { DateOverrideList } from "./DateOverrideList";
import { DateOverrideForm } from "./DateOverrideForm";

type Tab = "schedule" | "overrides";

export function AvailabilityPanel() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>("schedule");
  const [selectedGutachterId, setSelectedGutachterId] = useState<string>("");
  const [showOverrideForm, setShowOverrideForm] = useState(false);

  // If the user is a gutachter, lock to their own ID
  const isGutachter = user?.role === "gutachter";

  const gutachterList = useQuery(
    api.appointments.index.listGutachter,
    isGutachter ? "skip" : undefined,
  );

  // Resolve effective gutachter ID
  const gutachterId = useMemo(() => {
    if (isGutachter) return user?.id ?? "";
    return selectedGutachterId;
  }, [isGutachter, user?.id, selectedGutachterId]);

  return (
    <div className="space-y-6">
      {/* Gutachter selector (only for non-gutachter roles) */}
      {!isGutachter && (
        <Card>
          <CardContent className="pt-6">
            <Label htmlFor="gutachter-selector" className="block text-sm font-medium mb-2">
              Gutachter auswählen
            </Label>
            {gutachterList === undefined ? (
              <Skeleton className="h-9 w-full max-w-sm" />
            ) : gutachterList.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Keine Gutachter in Ihrer Organisation gefunden.
              </p>
            ) : (
              <select
                id="gutachter-selector"
                value={selectedGutachterId}
                onChange={(e) => setSelectedGutachterId(e.target.value)}
                className="flex h-9 w-full max-w-sm rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="gutachter-selector"
              >
                <option value="">Gutachter auswählen...</option>
                {gutachterList.map((g: any) => (
                  <option key={g._id} value={g._id as string}>
                    {g.name} ({g.email})
                  </option>
                ))}
              </select>
            )}
          </CardContent>
        </Card>
      )}

      {/* Content (only when gutachter is selected) */}
      {gutachterId ? (
        <>
          {/* Tab bar */}
          <div className="flex gap-1 rounded-lg bg-muted p-1">
            <TabButton
              active={activeTab === "schedule"}
              onClick={() => setActiveTab("schedule")}
              icon={<CalendarDays className="h-4 w-4" />}
              label="Wochenplan"
            />
            <TabButton
              active={activeTab === "overrides"}
              onClick={() => setActiveTab("overrides")}
              icon={<CalendarOff className="h-4 w-4" />}
              label="Abwesenheiten"
            />
          </div>

          {/* Tab content */}
          {activeTab === "schedule" && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Wöchentlicher Zeitplan</CardTitle>
              </CardHeader>
              <CardContent>
                <WeeklyScheduleEditor gutachterId={gutachterId} />
              </CardContent>
            </Card>
          )}

          {activeTab === "overrides" && (
            <div className="space-y-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-lg">Abwesenheiten</CardTitle>
                  <button
                    onClick={() => setShowOverrideForm(!showOverrideForm)}
                    className="rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium hover:bg-accent transition-colors"
                  >
                    {showOverrideForm
                      ? "Formular ausblenden"
                      : "Abwesenheit hinzufügen"}
                  </button>
                </CardHeader>
                <CardContent className="space-y-6">
                  {showOverrideForm && (
                    <div className="rounded-lg border bg-muted/30 p-4">
                      <DateOverrideForm
                        gutachterId={gutachterId}
                        onCreated={() => setShowOverrideForm(false)}
                      />
                    </div>
                  )}
                  <DateOverrideList gutachterId={gutachterId} />
                </CardContent>
              </Card>
            </div>
          )}
        </>
      ) : (
        !isGutachter && (
          <Card>
            <CardContent className="flex flex-col items-center py-12 text-center">
              <Loader2 className="h-8 w-8 text-muted-foreground mb-3 opacity-50" />
              <p className="text-muted-foreground">
                Bitte wählen Sie einen Gutachter aus, um die Verfügbarkeit zu
                verwalten.
              </p>
            </CardContent>
          </Card>
        )
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
        active
          ? "bg-background text-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}
